import { Link } from "react-router-dom";
import { PublicLayout } from "../../components/public-layout.js";
import {
  HeroSection,
  CTASection,
} from "../../components/marketing-components.js";

export function HowItWorksPage() {
  return (
    <PublicLayout>
      <HeroSection
        title="How AccessiMonitor works"
        subtitle="A step-by-step guide to accessibility monitoring"
      />

      <section className="marketing-section">
        <div className="section-content">
          <h2>Step by step</h2>

          <div className="steps-container">
            <div className="step">
              <h3>1. Add your website</h3>
              <p>
                Create an account and add your domain. We'll ask you to verify
                ownership via a DNS record or meta tag. This takes just a few
                minutes.
              </p>
            </div>

            <div className="step">
              <h3>2. Templates detected automatically</h3>
              <p>
                We automatically detect your site's page templates — homepages,
                category pages, product pages, forms. This helps us scan the
                right pages and group findings meaningfully.
              </p>
            </div>

            <div className="step">
              <h3>3. Set scan preferences</h3>
              <p>
                Choose your scan depth (how many pages we crawl), WCAG standard,
                and scanning frequency. We'll start monthly monitoring by
                default, or you can run scans on-demand.
              </p>
            </div>

            <div className="step">
              <h3>4. Initial scan runs</h3>
              <p>
                Our rendering engine loads your pages in a real browser (not
                just parsing HTML). We crawl templates you defined and scan them
                with axe, Pa11y, and custom checks.
              </p>
            </div>

            <div className="step">
              <h3>5. Review findings</h3>
              <p>
                Issues are grouped by impact and page type. Each finding shows:
                impact level, which rule failed, a screenshot, HTML context, and
                remediation guidance.
              </p>
            </div>

            <div className="step">
              <h3>6. Generate reports</h3>
              <p>
                We auto-generate an accessibility statement with your actual
                findings, timelines, and remediation commitments. You can also
                download detailed audit reports.
              </p>
            </div>

            <div className="step">
              <h3>7. Scan documents</h3>
              <p>
                We detect PDFs and documents on your site. If enabled, we
                analyze them separately for accessibility (PDF forms, text
                extraction, embedded images).
              </p>
            </div>

            <div className="step">
              <h3>8. Monitor month to month</h3>
              <p>
                Monthly scans check your progress. You see what's new, what's
                fixed, and what's still unresolved. Track remediation and prove
                ongoing commitment.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="marketing-section marketing-section--alt">
        <div className="section-content">
          <h2>What we scan, what you need to do</h2>

          <div className="two-columns">
            <div>
              <h3>Automated scanning finds:</h3>
              <ul>
                <li>Missing alt text on images</li>
                <li>Keyboard navigation issues</li>
                <li>Color contrast problems</li>
                <li>Form label and heading structure</li>
                <li>ARIA misuse</li>
                <li>Video caption and transcript needs</li>
                <li>PDF text extraction issues</li>
                <li>Link purpose clarity</li>
              </ul>
            </div>

            <div>
              <h3>Manual review still matters for:</h3>
              <ul>
                <li>Screen reader user testing</li>
                <li>Mobile/touch accessibility</li>
                <li>Content clarity and plain language</li>
                <li>Functionality edge cases</li>
                <li>Customer feedback integration</li>
                <li>Business logic accessibility</li>
                <li>Video quality and accuracy of captions</li>
                <li>Emerging assistive tech patterns</li>
              </ul>
            </div>
          </div>

          <p className="mt-4 text-muted" style={{ fontStyle: "italic" }}>
            We're transparent: automated testing catches many common issues, but
            real user testing and manual review are essential for full
            compliance confidence.
          </p>

          <div className="mt-4 text-center">
            <Link to="/manual-review">
              Learn about manual review services →
            </Link>
          </div>
        </div>
      </section>

      <section className="marketing-section">
        <div className="section-content">
          <h2>Real-world example</h2>

          <div className="example-flow">
            <div className="example-card">
              <h4>Month 1</h4>
              <p>
                <strong>Baseline scan:</strong> 47 issues found across 150
                pages, mostly color contrast (12), missing alt text (18), form
                labels (8). Statement generated showing commitment to remedy.
              </p>
            </div>

            <div className="example-arrow">→</div>

            <div className="example-card">
              <h4>Month 2 (after fixes)</h4>
              <p>
                <strong>Follow-up scan:</strong> 15 new issues, 28 resolved, 4
                persisting. Report shows progress in alt text remediation, but
                color contrast still needs work on component library.
              </p>
            </div>

            <div className="example-arrow">→</div>

            <div className="example-card">
              <h4>Month 3 (full remediation)</h4>
              <p>
                <strong>Clean scan:</strong> 3 remaining issues (acceptable
                risks noted). You now have 3 months of proof of ongoing
                commitment. Statement updated with resolution timeline.
              </p>
            </div>
          </div>
        </div>
      </section>

      <CTASection
        title="Ready to start monitoring?"
        description="Begin your free trial and set up your first website in minutes."
        primaryCTA={{ label: "Start now", href: "/sign-in" }}
      />
    </PublicLayout>
  );
}
